import { useQuery } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { Instrument, InstrumentFilters } from '../../models/instrument';
import { API_ENDPOINTS } from '../endpoints';
import { PaginatedResponse } from '../types';

// Get all CRM instruments with filtering
const fetchCrmInstruments = async (filters?: InstrumentFilters) => {
    const { data } = await httpClient.get<PaginatedResponse<Instrument>>(
        API_ENDPOINTS.CRM.INSTRUMENTS,
        { params: filters }
    );
    return data;
};

// Get all trader instruments with filtering
const fetchTraderInstruments = async (filters?: InstrumentFilters) => {
    const { data } = await httpClient.get<PaginatedResponse<Instrument>>(
        API_ENDPOINTS.TRADER.INSTRUMENTS,
        { params: filters }
    );
    return data;
};

// CRM Instruments hook (for CRM pages)
export const useCrmInstruments = (filters?: InstrumentFilters) => {
    return useQuery<PaginatedResponse<Instrument>, Error>({
        queryKey: [API_ENDPOINTS.CRM.INSTRUMENTS, filters],
        queryFn: () => fetchCrmInstruments(filters),
        staleTime: 5 * 60 * 1000, // 5 minutes - instruments don't change often
    });
};

// Trader Instruments hook (for trader pages)
export const useTraderInstruments = (filters?: InstrumentFilters) => {
    return useQuery<PaginatedResponse<Instrument>, Error>({
        queryKey: [API_ENDPOINTS.TRADER.INSTRUMENTS, filters],
        queryFn: () => fetchTraderInstruments(filters),
        staleTime: 5 * 60 * 1000, // 5 minutes - instruments don't change often
    });
};

// Legacy hook for backward compatibility - defaults to CRM instruments
export const useInstruments = (filters?: InstrumentFilters) => {
    return useCrmInstruments(filters);
};

// Get instrument by ID
const fetchInstrument = async (id: string) => {
    const { data } = await httpClient.get<ApiResponse<Instrument>>(
        `${API_ENDPOINTS.TRADER.INSTRUMENTS}/${id}`
    );
    return data.data;
};

export const useInstrument = (id: string) => {
    return useQuery<Instrument, Error>({
        queryKey: [API_ENDPOINTS.TRADER.INSTRUMENTS, id],
        queryFn: () => fetchInstrument(id),
        enabled: !!id,
        staleTime: 5 * 60 * 1000, // 5 minutes - instruments don't change often
    });
};

// Get market status for a specific instrument
const fetchInstrumentMarketStatus = async (instrumentId: string) => {
    const { data } = await httpClient.get<
        ApiResponse<{
            instrument_id: string;
            symbol: string;
            instrument_type: string;
            is_trading_open: boolean;
            status_text: string;
            current_time_utc: string;
            last_update: string;
            trading_hours?: any;
            next_opening?: string;
            next_closing?: string;
            market_info?: {
                type: string;
                trading_hours?: string;
                trading_days?: string;
                timezone?: string;
                maintenance_window?: string;
                note?: string;
            };
            current_price?: number;
            base_asset?: string;
            quote_asset?: string;
        }>
    >(API_ENDPOINTS.TRADER.INSTRUMENT_MARKET_STATUS(instrumentId));
    return data.data;
};

export const useInstrumentMarketStatus = (instrumentId: string) => {
    return useQuery({
        queryKey: [API_ENDPOINTS.TRADER.INSTRUMENT_MARKET_STATUS(instrumentId)],
        queryFn: () => fetchInstrumentMarketStatus(instrumentId),
        enabled: !!instrumentId,
        refetchInterval: 60 * 1000, // Refresh every minute
    });
};
